import React, { useState } from "react";
import { Input } from "reactstrap";
import { reasonLabel } from "./FeedbackRatingsModal";

const TOP_SECTIONS = 8;
const pct = (value) => (value == null ? "—" : `${Math.round(value)}%`);

function Bar({ value }) {
  return (
    <span className="fb-bar" aria-hidden="true">
      <span style={{ width: `${Math.min(100, value || 0)}%` }} />
    </span>
  );
}

function SectionRow({ row, onLook }) {
  return (
    <li className="fb-section-row">
      <button type="button" className="fb-row-main" onClick={() => onLook({ section: row.section, title: row.section })}>
        <span className="fb-row-name">{row.section}</span>
        <Bar value={row.pct} />
        <span className="fb-row-pct">{pct(row.pct)} ›</span>
      </button>
      {row.topReason && (
        <button type="button" className="fb-row-sub"
          onClick={() => onLook({ section: row.section, reason: row.topReason.reason, title: `${reasonLabel(row.topReason.reason)} in ${row.section}` })}>
          Mostly: {reasonLabel(row.topReason.reason)} › see comments
        </button>
      )}
    </li>
  );
}

// Section 02's reason list, section list and reason x section heatmap. Section
// names are per template, so only the busiest are listed until "Show all".
export default function FeedbackBreakdown({ breakdown, onLook }) {
  const [showAll, setShowAll] = useState(false);
  const [search, setSearch] = useState("");
  const { reasons, sections, heatmap } = breakdown;
  const query = search.trim().toLowerCase();
  const listed = showAll
    ? sections.filter((row) => !query || row.section.toLowerCase().includes(query))
    : sections.slice(0, TOP_SECTIONS);
  // Scaled to the named sections only: "All other sections" sums the long tail
  // and would wash out every clickable box.
  const max = Math.max(1, ...heatmap.rows.flatMap((row) => row.cells));

  return (
    <>
      <div className="fb-two-col fb-breakdown-panels">
        <div className="note-intel-panel fb-panel fb-compact-panel">
          <div className="note-intel-panel-header">
            <div>
              <h3>Why PTs gave a thumbs down</h3>
              <p>Click a reason to see the details behind it.</p>
            </div>
          </div>
          {reasons.length ? <ul className="fb-bar-list">
            {reasons.map((row) => (
              <li key={row.reason}>
                <button type="button" className="fb-row-main" onClick={() => onLook({ reason: row.reason, title: reasonLabel(row.reason) })}>
                  <span className="fb-row-name">{reasonLabel(row.reason)}</span>
                  <Bar value={row.pct} />
                  <span className="fb-row-pct">{pct(row.pct)}</span>
                </button>
              </li>
            ))}
          </ul> : <p className="fb-muted">No thumbs downs with a reason in these filters yet.</p>}
          <p className="fb-footnote">% of thumbs downs with a reason picked. PTs can pick more than one, so it adds up to more than 100%.</p>
        </div>

        <div className="note-intel-panel fb-panel fb-compact-panel">
          <div className="note-intel-panel-header">
            <div>
              <h3>Which parts of the note had problems</h3>
              <p>Click a section for details. % of thumbs downs that flagged a section.</p>
            </div>
          </div>
          {showAll && <Input type="search" className="fb-section-search" placeholder={`Search ${sections.length} sections`}
            aria-label="Search sections" value={search} onChange={(event) => setSearch(event.target.value)} />}
          {listed.length ? <ul className={`fb-bar-list${showAll ? " fb-scroll" : ""}`}>
            {listed.map((row) => <SectionRow key={row.section} row={row} onLook={onLook} />)}
          </ul> : <p className="fb-muted">{sections.length ? "No sections match." : "No thumbs downs flagged a section yet."}</p>}
          {sections.length > TOP_SECTIONS && (
            <button type="button" className="fb-show-all" onClick={() => { setShowAll(!showAll); setSearch(""); }}>
              {showAll ? `Show the top ${TOP_SECTIONS}` : `Show all ${sections.length} sections`}
            </button>
          )}
          {showAll && breakdown.totalSections > sections.length && (
            <p className="fb-footnote">Listing the {sections.length} busiest of {breakdown.totalSections} sections.</p>
          )}
        </div>
      </div>

      {!!heatmap.sections.length && (
        <div className="note-intel-panel fb-panel">
          <div className="note-intel-panel-header">
            <div>
              <h3>Which problems show up in which sections</h3>
              <p>Darker = more thumbs downs. Click a box for details.
                {heatmap.hasOther && ` The ${heatmap.sections.length} busiest sections, with the rest in "All other sections".`}</p>
            </div>
          </div>
          <div className="fb-heatmap-scroll">
            <table className="fb-heatmap">
              <thead>
                <tr>
                  <th scope="col">Section</th>
                  {heatmap.rows.map((row) => <th scope="col" key={row.reason}>{reasonLabel(row.reason)}</th>)}
                </tr>
              </thead>
              <tbody>
                {heatmap.sections.map((section, index) => (
                  <tr key={section}>
                    <th scope="row">{section}</th>
                    {heatmap.rows.map((row) => {
                      const value = row.cells[index];
                      return (
                        <td key={row.reason}>
                          <button type="button" disabled={!value} style={{ "--fb-heat": value / max }}
                            aria-label={`${value} thumbs downs: ${reasonLabel(row.reason)} in ${section}`}
                            onClick={() => onLook({ reason: row.reason, section, title: `${reasonLabel(row.reason)} in ${section}` })}>
                            {value}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
                {heatmap.hasOther && <tr>
                  <th scope="row">All other sections</th>
                  {heatmap.rows.map((row) => (
                    <td key={row.reason}><span className="fb-heat-static" style={{ "--fb-heat": Math.min(1, row.other / max) }}>{row.other}</span></td>
                  ))}
                </tr>}
              </tbody>
            </table>
          </div>
          <p className="fb-footnote">Each number = thumbs downs where the PT picked both that problem and that section.</p>
        </div>
      )}
    </>
  );
}
